import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Scrollable marquee banner.
 *
 * Renders ONE long horizontally-scrolling strip containing every active
 * banner message back-to-back so the owner can advertise multiple things.
 * CSS animation handles the scroll — duration scales with content length
 * so longer strips don't move faster.
 */
export default function MarketingBanner() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/marketing/banners")
      .then(({ data }) => { if (alive) setItems(Array.isArray(data) ? data : []); })
      .catch(() => { /* never crash a public page on this */ });
    return () => { alive = false; };
  }, []);

  if (!items.length) return null;

  // Build the strip. Duplicate the run twice so the scroll loops seamlessly.
  const run = items.map((it, i) => (
    <span key={`${it.id}-${i}`} className="inline-flex items-center gap-3 px-6 py-2.5">
      <span className="font-bold text-sm sm:text-base whitespace-nowrap">{it.message}</span>
      {it.cta_url && it.cta_label && (
        <a
          href={it.cta_url}
          className="underline underline-offset-2 text-sm sm:text-base whitespace-nowrap hover:opacity-90"
          data-testid={`banner-cta-${it.id}`}
        >
          {it.cta_label} →
        </a>
      )}
      <span className="opacity-40 px-2">•</span>
    </span>
  ));

  // ~3 seconds per item for a calm scroll, min 18s for legibility.
  const duration = Math.max(18, items.length * 6);
  // The first banner's colors set the strip palette. Owner can reorder.
  const bg = items[0]?.bg_color || "#236f6b";
  const fg = items[0]?.text_color || "#ffffff";

  return (
    <div
      className="w-full overflow-hidden relative"
      style={{ background: bg, color: fg }}
      data-testid="marketing-banner"
    >
      <div
        className="flex whitespace-nowrap will-change-transform marketing-banner-track"
        style={{ animationDuration: `${duration}s` }}
      >
        <div className="inline-flex items-center">{run}</div>
        <div className="inline-flex items-center" aria-hidden="true">{run}</div>
      </div>
      <style>{`
        @keyframes marketing-banner-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .marketing-banner-track {
          animation-name: marketing-banner-scroll;
          animation-timing-function: linear;
          animation-iteration-count: infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .marketing-banner-track { animation: none; }
        }
      `}</style>
    </div>
  );
}
